class CartDrawer extends HTMLElement {
  constructor() { 
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    if (window.location.hash === '#cart-drawer') {
      this.open();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Check if this is triggered by product add to cart on mobile
    const isProductAddTrigger = triggeredBy && (
      triggeredBy.closest('.product-form') || 
      triggeredBy.closest('.select-add') ||
      triggeredBy.closest('product-form') ||
      triggeredBy.closest('select-add')
    );
    
    // Add slide-from-bottom class on mobile for product add triggers
    const shouldSlideFromBottom = isProductAddTrigger && window.innerWidth < 768;
    if (shouldSlideFromBottom) {
      this.classList.add('slide-from-bottom');
    }

    // Delay activation by 1s only for bottom-slide opens; otherwise activate immediately
    if (this.classList.contains('slide-from-bottom')) {
      setTimeout(() => {
        this.classList.add('animate', 'active');
      }, 1000);
    } else {
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
    }

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Initialize upsell slider after drawer finishes opening (if not empty)
        // Only if not already initialized
        if (!this.classList.contains('is-empty') && window.initCartUpsellSlider) {
          clearTimeout(this._upsellOpenTimeout);
          this._upsellOpenTimeout = setTimeout(() => {
            window.initCartUpsellSlider(false); // Don't force, check if already initialized
          }, 500);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Check if drawer was opened with slide-from-bottom
    const wasSlideFromBottom = this.classList.contains('slide-from-bottom');
    
    if (wasSlideFromBottom) {
      // For bottom-slide drawers, remove active first, then slide-from-bottom after transition
      this.classList.remove('active');
      
      this.addEventListener(
        'transitionend',
        () => {
          // Add a small delay to make the closing animation slower
          setTimeout(() => {
            this.classList.remove('slide-from-bottom');
          }, 200);
        },
        { once: true }
      );
    } else {
      // For regular drawers, remove both classes immediately
      this.classList.remove('active', 'slide-from-bottom');
    }
    
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (window.location.hash === '#cart-drawer') {
      const newUrl = window.location.href.replace('#cart-drawer', '');
      history.replaceState(null, null, newUrl);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const htmlContent = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      sectionElement.innerHTML = htmlContent;
      
      // Execute any scripts in the HTML content
      const scripts = sectionElement.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    });

    // Initialize upsell slider after sections are rendered (before opening drawer)
    // Wait a bit for scripts to execute
    setTimeout(() => {
      if (window.initCartUpsellSlider) {
        console.log('[Cart Drawer] renderContents: Setting up slider initialization after render');
        clearTimeout(this._upsellRenderTimeout);
        this._upsellRenderTimeout = setTimeout(() => {
          console.log('[Cart Drawer] renderContents: Calling initCartUpsellSlider (after render)');
          window.initCartUpsellSlider(true); // Force reinit since cart was rerendered
        }, 300);
      } else {
        console.log('[Cart Drawer] renderContents: initCartUpsellSlider function not available, retrying...');
        // Retry after a longer delay in case script is still loading
        setTimeout(() => {
          if (window.initCartUpsellSlider) {
            console.log('[Cart Drawer] renderContents: initCartUpsellSlider now available, calling it');
            window.initCartUpsellSlider(true);
          } else {
            console.log('[Cart Drawer] renderContents: initCartUpsellSlider still not available');
          }
        }, 1000);
      }
    }, 100);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      // Add-to-cart path: ensure mobile bottom animation is applied before opening
      if (window.innerWidth < 768) this.classList.add('slide-from-bottom');

      this.open();
      
      // Also initialize after drawer opens (backup in case first one didn't catch it)
      if (window.initCartUpsellSlider) {
        clearTimeout(this._upsellInitTimeout);
        this._upsellInitTimeout = setTimeout(() => {
          console.log('[Cart Drawer] renderContents: Calling initCartUpsellSlider (after open)');
          window.initCartUpsellSlider(true); // Force reinit since cart was rerendered
        }, 1500);
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    console.log('[Cart Drawer Items] onCartUpdate called');
    // Call parent's onCartUpdate first
    super.onCartUpdate();
    
    // Initialize upsell slider after cart update (only once, with delay)
    if (window.initCartUpsellSlider) {
      console.log('[Cart Drawer Items] onCartUpdate: Setting up slider initialization');
      clearTimeout(this._upsellUpdateTimeout);
      this._upsellUpdateTimeout = setTimeout(() => {
        console.log('[Cart Drawer Items] onCartUpdate: Calling initCartUpsellSlider');
        window.initCartUpsellSlider(true); // Force reinit since cart was updated
      }, 1000);
    } else {
      console.log('[Cart Drawer Items] onCartUpdate: initCartUpsellSlider function not available');
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
